import React, { useState } from "react";

import {
  Table,
  Form,
  Input,
  InputNumber,
  Tag,
  message as antdMessage,
  Alert,
  Skeleton,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { Plus } from "lucide-react";

import type { ColumnsType } from "antd/es/table";

import FormModal from "../../common/modal/FormModal";

import { TaxRate } from "../../../bitmaker/schemas/Billing/TaxRate";

import { useApiError } from "../../../hooks/useApiError";
import { useTaxRates } from "../../../hooks/useTaxRates";

import { HTTPMethod, methodMap } from "../../../services/BitmakerApp";

import { MyFormItem } from "../MyFormItem";

export default function TaxRatesManagement() {
  const { rates, loadingTaxRates, fetchTaxRatesError, refetchTaxRates } =
    useTaxRates();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const [editingTax, setEditingTax] = useState<TaxRate | null>(null);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { handleAPIError } = useApiError();

  const handleEditModal = (record: TaxRate) => {
    setEditingTax(record);

    const currentValues = {
      ...record,
      name: record.name.trim(),
      description: record.description?.trim() || "",
      rate: record.rate * 100,
    };

    form.setFieldsValue(currentValues);

    setIsModalOpen(true);
    setSubmitError(null);
  };

  const handleAddModal = () => {
    setEditingTax(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleCancelModal = () => {
    setSubmitError(null);
    setIsModalOpen(false);
  };

  const getTaxRateErrorMessage = (
    status: number | null,
    isEditing: boolean,
  ) => {
    if (status === 409) {
      return "A tax rate with this name already exists.";
    }

    return `Error ${isEditing ? "updating" : "creating"
      } tax rate. Please try again.`;
  };

  const handleSubmit = (values: {
    name: string;
    rate: number;
    description?: string;
  }) => {
    if (isSubmitting) return;

    setSubmitError(null);
    setIsSubmitting(true);

    const dataToSend = {
      ...values,
      name: values.name.trim(),
      rate: (values.rate / 100).toString(),
      description: values.description?.trim() || null,
    };

    const isEditing = editingTax !== null;

    const endpoint = isEditing
      ? { method: "put", url: `/billing/settings/tax-rates/${editingTax?.id}` }
      : { method: "post", url: "/billing/settings/tax-rates" };

    const method = endpoint.method as HTTPMethod;

    methodMap[method](endpoint.url, dataToSend, (response) => {
      const { isError, status } = handleAPIError(response);

      if (isError) {
        const errorMessage = getTaxRateErrorMessage(status, isEditing);
        setSubmitError(errorMessage);
        setIsSubmitting(false);
        return;
      }

      antdMessage.success(
        `Tax Rate "${response.data.name}" ${isEditing ? "updated" : "created"
        } successfully!`,
      );

      setIsModalOpen(false);
      setEditingTax(null);
      refetchTaxRates();
      setIsSubmitting(false);
    });
  };

  const columns: ColumnsType<TaxRate> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
      render: (name: string) => (
        <span className="font-medium text-gray-700">{name}</span>
      ),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: "15%",
      render: (rate: number) => (
        <Tag color="blue">
          {(rate * 100).toLocaleString("en-US", {
            maximumFractionDigits: 2,
          })}
          %
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "45%",
      render: (description?: string) => description || "—",
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      render: (_, record) => (
        <button
          type="button"
          onClick={() => handleEditModal(record)}
          className="btn btn-small btn-indigo"
          disabled={loadingTaxRates}
        >
          <EditOutlined size={14} />
          <span>Edit</span>
        </button>
      ),
    },
  ];

  const isInitialLoading = loadingTaxRates && rates.length === 0;
  const fetchError = !!fetchTaxRatesError;

  return (
    <div>
      {/* Header + primary action */}
      <div className="flex justify-between items-start gap-4 mb-6">
        <div className="flex flex-col gap-1 min-w-0">
          <h2 className="text-2xl text-gray-800">Tax Rates</h2>
          <p className="text-gray-600 text-sm">
            Define tax rates used on invoice lines (e.g. IGV, IVA). Enter them
            as percentages in the form.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddModal}
          className="btn btn-green-2 shrink-0"
          disabled={loadingTaxRates}
        >
          <Plus size={16} />
          <span>Add Tax Rate</span>
        </button>
      </div>

      {/* Error */}
      {fetchError && (
        <div className="mb-4">
          <Alert
            type="error"
            showIcon
            message="Failed to load tax rates"
            description="Something went wrong while fetching data. Please try again."
            action={
              <button
                type="button"
                className="btn btn-gray"
                onClick={refetchTaxRates}
                disabled={loadingTaxRates}
              >
                Retry
              </button>
            }
          />
        </div>
      )}

      {/* Table / Loading */}
      {isInitialLoading ? (
        <div className="mt-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="py-2">
              <Skeleton
                active
                title={false}
                paragraph={{ rows: 1, width: "100%" }}
              />
            </div>
          ))}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={rates}
          rowKey="id"
          loading={loadingTaxRates}
          pagination={false}
          rowClassName={() => "hover:bg-gray-50 transition-colors"}
          locale={{
            emptyText: fetchError ? (
              "Unable to load tax rates"
            ) : (
              <div className="py-10 flex flex-col items-center justify-center gap-3 text-center">
                <div className="text-gray-500 text-sm">
                  You don't have any tax rates yet
                </div>
                <button
                  type="button"
                  className="btn btn-gray"
                  onClick={handleAddModal}
                  disabled={loadingTaxRates}
                >
                  <Plus size={16} />
                  <span>Add Tax Rate</span>
                </button>
              </div>
            ),
          }}
          bordered
        />
      )}

      {/* Add/Edit Tax Rate Form Modal */}
      <FormModal
        title={
          editingTax ? `Edit Tax Rate: ${editingTax.name}` : "Add New Tax Rate"
        }
        open={isModalOpen}
        onCancel={handleCancelModal}
        form={form}
        formId="addTaxForm"
        onFinish={handleSubmit}
        isSubmitting={isSubmitting}
        submitLabel={editingTax ? "Update" : "Save"}
        error={submitError}
        afterClose={() => {
          form.resetFields();
          setEditingTax(null);
          setSubmitError(null);
        }}
      >
        <MyFormItem
          label="Tax Name"
          name="name"
          rules={[
            {
              required: true,
              message: "Please enter the tax name (e.g., IGV, IVA).",
            },
          ]}
        >
          <Input
            placeholder="IGV, IVA..."
            onChange={() => submitError && setSubmitError(null)}
            onBlur={(e) => {
              const value = e.target.value.trim();
              if (value !== e.target.value) {
                form.setFieldsValue({ name: value });
              }
            }}
          />
        </MyFormItem>

        <MyFormItem
          label="Rate (%)"
          name="rate"
          tooltip="Enter the rate as a percentage (e.g., enter 18 for 18%)."
          rules={[
            {
              required: true,
              message: "Please enter the tax rate percentage.",
            },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            step={0.01}
            precision={2}
            style={{ width: "100%" }}
            onChange={() => submitError && setSubmitError(null)}
          />
        </MyFormItem>

        <MyFormItem label="Description" name="description">
          <Input.TextArea
            rows={2}
            placeholder="Describe the tax..."
            onChange={() => submitError && setSubmitError(null)}
          />
        </MyFormItem>
      </FormModal>
    </div>
  );
}
